import { Suspense, lazy, useState, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "react-hot-toast";
import {
  Routes,
  Route,
  NavLink,
  Navigate,
  useLocation,
} from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { WebSocketProvider } from "./contexts/WebSocketContext";
import {
  Bars3Icon,
  HomeIcon,
  MagnifyingGlassIcon,
  FireIcon,
} from "@heroicons/react/24/outline";
import { getImageWithFallback } from "./utils/imageCache";
import axios from "axios";
import { configureAxiosRetry } from "./utils/axiosRetry";
import TagManager from "react-gtm-module";

// Configure axios with retry
configureAxiosRetry(axios);

// Lazy load pages and heavy components
const TokensPage = lazy(() => import("./pages/TokensPage"));
const ChannelsPage = lazy(() => import("./pages/ChannelsPage"));
const ChannelDetailsPage = lazy(() => import("./pages/ChannelDetailsPage"));
const ReferralPage = lazy(() => import("./pages/ReferralPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const TopCallsPage = lazy(() => import("./pages/TopCallsPage"));
const SettingsPanel = lazy(() => import("./components/SettingsPanel"));
const SearchPanel = lazy(() => import("./components/SearchPanel"));
const SocialsAside = lazy(() => import("./components/SocialsAside"));

// Configure React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false,
      staleTime: 60000,
      cacheTime: 5 * 60 * 1000,
    },
  },
});

// Loading fallback component
const LoadingFallback = () => (
  <div className="flex justify-center items-center min-h-screen">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold-500" />
  </div>
);

// Header component with location awareness
function Header({ onSearchClick, onSettingsClick }) {
  const location = useLocation();
  const isSettingsPage = location.pathname === "/settings";

  return (
    <header className="border-b border-background-500">
      <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 py-2">
        <div className="h-14 flex items-center justify-between">
          <div className="flex items-center">
            <img
              src={getImageWithFallback("/logo.svg", "/logo.svg")}
              alt="Logo"
              className="h-[2.4rem] w-auto"
              loading="eager"
              crossOrigin="anonymous"
            />
          </div>
          <nav className="hidden min-[800px]:flex space-x-4">
            <NavLink
              to="/"
              className={({ isActive }) =>
                `relative px-3 py-2 rounded-md text-sm font-medium ${
                  isActive
                    ? "text-gold-400"
                    : "text-background-300 hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <div className="flex items-center space-x-2">
                    <HomeIcon
                      className={`h-5 w-5 ${isActive ? "text-gold-400" : ""}`}
                    />
                    <span>Dashboard</span>
                  </div>
                  {isActive && (
                    <div className="absolute bottom-0 left-0 w-full h-0.5 bg-gold-400" />
                  )}
                </>
              )}
            </NavLink>
            {/* <NavLink
              to="/channels"
              className={({ isActive }) =>
                `relative px-3 py-2 rounded-md text-sm font-medium ${
                  isActive
                    ? "text-gold-400"
                    : "text-background-300 hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <div className="flex items-center space-x-2">
                    <SignalIcon
                      className={`h-5 w-5 ${isActive ? "text-gold-400" : ""}`}
                    />
                    <span>Channels</span>
                  </div>
                  {isActive && (
                    <div className="absolute bottom-0 left-0 w-full h-0.5 bg-gold-400" />
                  )}
                </>
              )}
            </NavLink> */}
            <NavLink
              to="/topcalls"
              className={({ isActive }) =>
                `relative px-3 py-2 rounded-md text-sm font-medium ${
                  isActive
                    ? "text-gold-400"
                    : "text-background-300 hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <div className="flex items-center space-x-2">
                    <FireIcon
                      className={`h-5 w-5 ${isActive ? "text-gold-400" : ""}`}
                    />
                    <span>Top Calls</span>
                  </div>
                  {isActive && (
                    <div className="absolute bottom-0 left-0 w-full h-0.5 bg-gold-400" />
                  )}
                </>
              )}
            </NavLink>
            {/* <NavLink
                to="/referral"
                className={({ isActive }) => 
                  `relative px-3 py-2 rounded-md text-sm font-medium ${
                    isActive
                      ? 'text-gold-400'
                      : 'text-background-300 hover:text-white'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <div className="flex items-center space-x-2">
                      <UserGroupIcon className={`h-5 w-5 ${isActive ? 'text-gold-400' : ''}`} />
                      <span>Invite & Earn</span>
                    </div>
                    {isActive && (
                      <div className="absolute bottom-0 left-0 w-full h-0.5 bg-gold-400" />
                    )}
                  </>
                )}
              </NavLink> */}
          </nav>
          <div className="flex items-center space-x-4">
            <button
              onClick={onSearchClick}
              className="p-2 rounded-md text-background-300 hover:text-white"
            >
              <MagnifyingGlassIcon className="h-5 w-5" />
            </button>
            <button
              onClick={onSettingsClick}
              className="p-2 rounded-md text-background-300 hover:text-white min-[800px]:hidden"
            >
              <Bars3Icon className="h-5 w-5 min-[800px]:hidden" />
              {/* <Cog6ToothIcon
                className={`h-5 w-5 hidden min-[800px]:block ${
                  isSettingsPage ? "text-gold-400" : ""
                }`}
              /> */}
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}

function App() {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  useEffect(() => {
    TagManager.initialize({ gtmId: "GTM-WKV3FVL2" });
  }, []);

  useEffect(() => {
    const handleUnhandledRejection = (event) => {
      console.error("Unhandled Promise Rejection:", event.reason);
      event.preventDefault();
    };

    window.addEventListener("unhandledrejection", handleUnhandledRejection);
    return () => {
      window.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection,
      );
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <WebSocketProvider>
          <Routes>
            <Route
              path="/login"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <LoginPage />
                </Suspense>
              }
            />
            <Route
              path="/*"
              element={
                // <ProtectedRoute>
                <div className="h-screen flex flex-col bg-background-800 overflow-hidden">
                  <Header
                    onSearchClick={() => setIsSearchOpen(true)}
                    onSettingsClick={() => setIsSettingsOpen(true)}
                  />

                  <div className="flex-1 max-w-[1920px] mx-auto w-full px-4 sm:px-6 lg:px-8 py-2 overflow-hidden">
                    <Suspense fallback={<LoadingFallback />}>
                      <Routes>
                        <Route path="/" element={<TokensPage />} />
                        <Route path="/channels" element={<ChannelsPage />} />
                        <Route
                          path="/channels/:channelId"
                          element={<ChannelDetailsPage />}
                        />
                        {/* <Route path="/referral" element={<ReferralPage />} /> */}
                        {/* <Route path="/settings" element={<SettingsPage />} /> */}
                        <Route path="/topcalls" element={<TopCallsPage />} />
                        <Route path="*" element={<Navigate to="/" replace />} />
                      </Routes>
                    </Suspense>
                  </div>
                  <SocialsAside />
                </div>
                // </ProtectedRoute>
              }
            />
          </Routes>

          <Suspense fallback={null}>
            <SearchPanel
              isOpen={isSearchOpen}
              onClose={() => setIsSearchOpen(false)}
            />
          </Suspense>

          <Suspense fallback={null}>
            <SettingsPanel
              isOpen={isSettingsOpen}
              onClose={() => setIsSettingsOpen(false)}
            />
          </Suspense>

          <Toaster
            position="bottom-right"
            toastOptions={{
              className: "custom-toast",
              success: {
                className: "custom-toast custom-toast-success",
              },
              error: {
                className: "custom-toast custom-toast-error",
              },
              duration: 3000,
            }}
          />
        </WebSocketProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
